import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { usePlacesStore } from "../stores/placesStore";

interface CreatePlanModalProps {
  onClose: () => void;
}

const DURATION_OPTIONS = [
  { value: 4, label: "4 weeks" },
  { value: 6, label: "6 weeks" },
  { value: 8, label: "8 weeks" },
  { value: 12, label: "12 weeks" },
  { value: 16, label: "16 weeks" },
];

export default function CreatePlanModal({ onClose }: CreatePlanModalProps) {
  const navigate = useNavigate();
  const { filters, pagination } = usePlacesStore();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [durationWeeks, setDurationWeeks] = useState(8);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!name.trim()) {
      setError("Please enter a plan name");
      return;
    }

    setIsCreating(true);
    setError("");

    try {
      const response = await api.createPlan({
        name: name.trim(),
        description: description.trim() || undefined,
        startDate: new Date().toISOString(),
        durationWeeks,
        filters,
      });

      navigate(`/plan/${response.data._id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create plan");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
      <div className="bg-white rounded-t-xl md:rounded-xl shadow-2xl w-full max-w-md mx-0 md:mx-4 max-h-[90vh] flex flex-col">
        <div className="p-4 md:p-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">
              Create Outreach Plan
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg
                className="w-5 h-5 md:w-6 md:h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto px-4 md:px-6 pb-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plan Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Q1 2024 Durham High Street Outreach"
              className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Brief description of the plan objectives..."
              className="w-full px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plan Duration
            </label>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {DURATION_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setDurationWeeks(option.value)}
                  className={`px-2 md:px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors ${
                    durationWeeks === option.value
                      ? "bg-primary-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <span className="font-medium">{pagination.total}</span> businesses
              match your current filters.
              {pagination.total > 0 && (
                <span>
                  {" "}
                  That's ~
                  <span className="font-medium">
                    {Math.ceil(pagination.total / durationWeeks)}
                  </span>{" "}
                  businesses per week over{" "}
                  <span className="font-medium">{durationWeeks}</span> weeks.
                </span>
              )}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Visits will be grouped by location for efficient travel.
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Applied Filters
            </h4>
            <div className="text-xs text-gray-500 space-y-1">
              {filters.search && <p>Search: {filters.search}</p>}
              {filters.category && <p>Category: {filters.category}</p>}
              {filters.location && <p>Location: {filters.location}</p>}
              {filters.status && <p>Status: {filters.status}</p>}
              {filters.minScore && <p>Min Score: {filters.minScore}</p>}
              {filters.maxScore && <p>Max Score: {filters.maxScore}</p>}
              {filters.noWebsite && <p>No Website: Yes</p>}
              {filters.noPhone && <p>No Phone: Yes</p>}
              {filters.noGoogleProfile && <p>No Google Profile: Yes</p>}
              {filters.recentlyOpened && <p>Recently Opened: Yes</p>}
              {filters.lowReviewVolume && <p>Low Review Volume: Yes</p>}
              {filters.lowFoodHygiene && <p>Low Food Hygiene: Yes</p>}
              {filters.poorGoogleRating && <p>Poor Google Rating: Yes</p>}
              {filters.contactedThisWeek && <p>Contacted This Week: Yes</p>}
              {filters.followUpDue && <p>Follow Up Due: Yes</p>}
              {filters.notEnriched && <p>Not Enriched: Yes</p>}
              {Object.keys(filters).filter(
                (k) =>
                  filters[k as keyof typeof filters] !== undefined &&
                  filters[k as keyof typeof filters] !== ""
              ).length === 0 && <p>No filters applied - using all places</p>}
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3 flex-shrink-0 safe-area-pb">
          <button
            onClick={onClose}
            className="px-4 py-2.5 md:py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating}
            className="px-4 py-2.5 md:py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {isCreating ? "Creating..." : "Create Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}
